import { mutate as globalMutate } from 'swr';
import useSWRImmutable from 'swr/immutable';

import { AppointmentChainConfiguration } from '@/schemas/admin/appointment-chain-configurations.schema.ts';

import { fetchChainConfigs } from '@/services/admin/chain-config-service.ts';
import { showErrorNotification } from '@/services/notificationService.ts';

const CHAIN_CONFIGS_CACHE_KEY = 'appointment_chain_configs';

type ChainConfigsCacheKey = [typeof CHAIN_CONFIGS_CACHE_KEY, string, boolean, boolean];

// Single source of truth for the SWR cache key. The boolean flags are normalized so callers
// that omit them (undefined) share a cache entry with callers that pass `false`.
const buildChainConfigsCacheKey = (
    customerId: string,
    showDeleted: boolean | undefined,
    includeEligiblePractitioners: boolean | undefined
): ChainConfigsCacheKey => [
    CHAIN_CONFIGS_CACHE_KEY,
    customerId,
    showDeleted === true,
    includeEligiblePractitioners === true,
];

const isChainConfigsCacheKey = (key: unknown, customerId: string): key is ChainConfigsCacheKey =>
    Array.isArray(key) && key[0] === CHAIN_CONFIGS_CACHE_KEY && key[1] === customerId;

/**
 * Invalidates the chain-config caches for a customer after a create/delete.
 *
 * Uses filter functions (matched against every cache key) rather than reconstructing exact keys, so
 * any param added to the key later is still covered. SWR has no public API to tell whether a key is
 * currently mounted, so we use the showDeleted flag (key[2]) as the proxy — it deterministically
 * identifies which list is on screen — and handle each accordingly:
 * - admin list (showDeleted=true) is mounted → revalidate so it refreshes in place (no data flash).
 * - booking lists (showDeleted=false) are unmounted → EVICT, because revalidating an unmounted key is
 *   a no-op and useSWRImmutable won't refetch a still-cached key on its next mount; clearing the cache
 *   forces fresh data when the booking flow reopens.
 */
export const revalidateChainConfigCaches = async (customerId: string): Promise<void> => {
    await Promise.all([
        globalMutate((key) => isChainConfigsCacheKey(key, customerId) && key[2]),
        globalMutate((key) => isChainConfigsCacheKey(key, customerId) && !key[2], undefined, {
            revalidate: false,
        }),
    ]);
};

interface UseAppointmentChainConfigsParams {
    customerId: string;
    showDeleted?: boolean;
    includeEligiblePractitioners?: boolean;
}

interface UseAppointmentChainConfigsResult {
    chainConfigs: AppointmentChainConfiguration[];
    isLoading: boolean;
    error: Error | undefined;
    mutate: () => Promise<AppointmentChainConfiguration[] | undefined>;
}

export const useAppointmentChainConfigs = ({
    customerId,
    showDeleted,
    includeEligiblePractitioners,
}: UseAppointmentChainConfigsParams): UseAppointmentChainConfigsResult => {
    const shouldFetch = !!customerId;
    const { data, error, isLoading, mutate } = useSWRImmutable<AppointmentChainConfiguration[], Error>(
        shouldFetch ? buildChainConfigsCacheKey(customerId, showDeleted, includeEligiblePractitioners) : undefined,
        () => fetchChainConfigs(customerId, { showDeleted, includeEligiblePractitioners }),
        { onError: () => showErrorNotification('Fehler beim Laden der Ketten.') }
    );
    return {
        chainConfigs: data ?? [],
        isLoading: isLoading,
        error: error,
        mutate: mutate,
    };
};
